import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { adjustWith2DecimalsForUI } from '../commonfunctions/commonFunction';

/**
 * AllStateCiTileComponent
 * ─────────────────────────────────────────────────────────────────────────────
 * Copy of LifeInsuranceTileComponent adapted for the All-State Critical Illness
 * 100k plan where:
 *   • MEMBER + no dependents  → one checkbox labelled "SINGLE"
 *   • MEMBER + spouse/children → one checkbox labelled "FAMILY"
 *   • Separate smoker/non-smoker radio for member (always) and for spouse (when present)
 *   • Checkbox always emits one event for category = 'member'; parent handles pricing
 *   • Backend currently only provides the `member` key → no `single`/`family` keys yet;
 *     UI is built ready for when those keys arrive.
 */
@Component({
  selector: 'app-allstate-ci-tile',
  templateUrl: './allstate-ci-tile.component.html',
  styleUrl: './allstate-ci-tile.component.scss',
})
export class AllstateCiTileComponent implements OnInit, OnChanges {
  // ── Inputs ────────────────────────────────────────────────────────────────
  @Input() plan: any;
  @Input() product: any;
  @Input() k: number = 0;
  @Input() coverage: any;
  @Input() pkg: any;        // "package" is reserved
  @Input() group: any;
  @Input() allFormData: any = {};
  @Input() lang: any = {};
  @Input() userForm: any;

  // ── Outputs ───────────────────────────────────────────────────────────────
  @Output() checkboxChangePEvent = new EventEmitter<any>();
  @Output() radioChangeEvent     = new EventEmitter<any>();
  @Output() rangeChangeEvent     = new EventEmitter<any>();

  // ── Per-tile isolated state ───────────────────────────────────────────────
  rangeValues:   any[]   = [];
  greenValue:    any     = 0;
  showyellowbar: boolean = false;

  // ── Unique per-tile key (addon id, NOT planCoverage which is shared) ──────
  get addonKey(): string {
    return String(
      this.plan?.productAddonss?.[this.product]?.[this.k]?.id
        ?? this.plan?.planCoverage
    );
  }

  /**
   * True  → user has no spouse AND no dependent children → show "SINGLE"
   * False → user has a spouse or dependent children      → show "FAMILY"
   */
  get isSinglePlan(): boolean {
    return this.userForm?.get('having_spouse')?.value    !== 'true'
        && this.userForm?.get('having_dependent_children')?.value !== 'true';
  }

  /** Whether the main (member) checkbox is currently checked */
  get isMemberChecked(): boolean {
    return !!(this.allFormData[this.addonKey]?.member);
  }

  /** True when spouse smoker row should be rendered */
  get hasSpouse(): boolean {
    return this.userForm?.get('having_spouse')?.value === 'true';
  }

  // ─────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.computeRangeValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plan'] || changes['k'] || changes['product']) {
      this.computeRangeValues();
    }
  }

  private computeRangeValues(): void {
    const member = this.plan?.productAddonss?.[this.product]?.[this.k]?.member;
    if (member) {
      this.rangeValues = this.generateTicks(member.maximumCoverageWithEvidence);
    }
  }

  // ── Slider helpers ────────────────────────────────────────────────────────

  public getSliderBackground(
    rangeValue:   any,
    maxRange:     any,
    _stepCount:   any,
    defaultRange: any
  ): string {
    if (rangeValue <= defaultRange) {
      this.greenValue    = (defaultRange / maxRange) * 100;
      this.showyellowbar = false;
      return `linear-gradient(to right, green ${this.greenValue}%, lightgray 0%)`;
    } else {
      const greenPart  = adjustWith2DecimalsForUI((defaultRange / maxRange) * 100);
      const yellowPart = adjustWith2DecimalsForUI((rangeValue   / maxRange) * 100);
      this.greenValue    = greenPart;
      this.showyellowbar = true;
      return `linear-gradient(to right,
        green     0%,
        green     ${greenPart}%,
        yellow    ${greenPart}%,
        yellow    ${yellowPart}%,
        lightgray ${yellowPart}%,
        lightgray 100%)`;
    }
  }

  /**
   * Always returns exactly 4 evenly-spaced tick values.
   * 100k → [0, 33333, 66667, 100000]  displayed as $0k · $100k  (intermediates hidden)
   * 300k → [0, 100000, 200000, 300000] displayed as $0k · $100k · $200k · $300k
   */
  public generateTicks(maxRange: any): any[] {
    if (!maxRange) { return []; }
    const interval = maxRange / 3;
    return [0, interval, interval * 2, maxRange];
  }

  // ── disabled-state helpers ────────────────────────────────────────────────

  /** Smoker radios disabled when the member checkbox is not yet checked */
  public isMemberRadioDisabled(): boolean {
    return !(this.allFormData[this.addonKey]?.member);
  }

  /** Spouse smoker radios disabled when the member checkbox is not yet checked */
  public isSpouseRadioDisabled(): boolean {
    return !(this.allFormData[this.addonKey]?.member);
  }

  // ── Event forwarders ──────────────────────────────────────────────────────

  onCheckboxChangePLocal(
    event:        any,
    planCoverage: string,
    category:     string,
    key1:         string,
    productAddon: any
  ): void {
    this.checkboxChangePEvent.emit({ event, planCoverage, category, key1, productAddon });
  }

  onRadioChangeLocal(
    event:        any,
    planCoverage: string,
    category:     string,
    planOptions:  any,
    planObj:      any,
    productAddon: any,
    pkgObj:       any,
    groupObj:     any,
    coverageData: any
  ): void {
    if (coverageData?.maximumCoverageWithEvidence) {
      this.rangeValues = this.generateTicks(coverageData.maximumCoverageWithEvidence);
    }
    this.radioChangeEvent.emit({
      event, planCoverage, category,
      planOptions, planObj, productAddon,
      pkgObj, groupObj, coverageData
    });
  }

  onRangeChangeLocal(
    event:        any,
    planCoverage: string,
    category:     string,
    planOptions:  any,
    planObj:      any,
    productAddon: any,
    pkgObj:       any,
    groupObj:     any,
    rangeEvent:   any,
    coverageData: any
  ): void {
    if (coverageData?.maximumCoverageWithEvidence) {
      this.rangeValues = this.generateTicks(coverageData.maximumCoverageWithEvidence);
    }
    this.rangeChangeEvent.emit({
      event, planCoverage, category,
      planOptions, planObj, productAddon,
      pkgObj, groupObj, rangeEvent, coverageData
    });
  }
}
